import { useEffect, useState } from "react";
import {
  FluentProvider,
  TabList,
  Tab,
  CounterBadge,
  Avatar,
  Badge,
  Dropdown,
  Option,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Divider,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  CalendarLtr24Regular,
  ClipboardTaskListLtr24Regular,
  DocumentAdd24Regular,
  PeopleTeam24Regular,
  SignOut24Regular,
} from "@fluentui/react-icons";
import { APP_VERSION_LABEL } from "./config/appVersion";
import { useTeamsTheme } from "./hooks/useTeamsTheme";
import { AppProvider, useApp } from "./context/AppContext";
import kationLogo from "./assets/brand/kation-logo-white.png";
import { FileLeave } from "./components/FileLeave";
import { MyLeaves } from "./components/MyLeaves";
import { ManagerQueue } from "./components/ManagerQueue";
import { TeamCalendar } from "./components/TeamCalendar";
import { AskKatie } from "./components/AskKatie";

const useStyles = makeStyles({
  app: {
    minHeight: "100vh",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  topbar: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  brand: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  logoPlate: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: "36px",
    padding: "0 8px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "#0A2A4A",
    marginRight: tokens.spacingHorizontalXS,
  },
  logoImg: { height: "26px", width: "auto", display: "block" },
  user: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM },
  viewAs: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalXS },
  viewAsLabel: { color: tokens.colorNeutralForeground3, whiteSpace: "nowrap" },
  tabs: {
    padding: `0 ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  content: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: tokens.spacingVerticalXL,
    // room for the fixed version label so it never sits on top of content
    paddingBottom: tokens.spacingVerticalXXXL,
  },
  version: {
    position: "fixed",
    right: tokens.spacingHorizontalM,
    bottom: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground4,
    // purely informational — never swallow a click meant for the UI beneath
    pointerEvents: "none",
    userSelect: "none",
  },
  tabLabel: { display: "inline-flex", alignItems: "center", gap: tokens.spacingHorizontalXS },
  avatarButton: {
    border: "none",
    background: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    borderRadius: "50%",
    display: "inline-flex",
    ":focus-visible": {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: "2px",
    },
  },
  menuHeader: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    maxWidth: "260px",
  },
  menuEmail: { color: tokens.colorNeutralForeground3, wordBreak: "break-all" },
});

type TabValue = "file" | "mine" | "queue" | "calendar";

const TABS: TabValue[] = ["file", "mine", "queue", "calendar"];

/**
 * Deep links from Teams/Outlook notifications: ?tab=queue opens the Manager
 * Queue, ?tab=mine opens My leaves. (MSAL returns to the original URL after
 * sign-in, so the tab survives the login redirect.)
 */
function tabFromUrl(): TabValue {
  const tab = new URLSearchParams(window.location.search).get("tab");
  return TABS.includes(tab as TabValue) ? (tab as TabValue) : "file";
}

function syncTabToUrl(tab: TabValue) {
  const url = new URL(window.location.href);
  url.searchParams.set("tab", tab);
  window.history.replaceState(null, "", url);
}

function Shell() {
  const s = useStyles();
  const {
    service,
    currentUser,
    employee,
    isManager,
    dataMode,
    refreshSignal,
    switchableUsers,
    switchUser,
    signOut,
    photoUrl,
  } = useApp();
  const [selected, setSelected] = useState<TabValue>(tabFromUrl);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isManager) {
      setPendingCount(0);
      return;
    }
    let cancelled = false;
    service.getPendingForManager(currentUser.email).then((rows) => {
      if (!cancelled) setPendingCount(rows.length);
    });
    return () => {
      cancelled = true;
    };
    // `selected` is a dep so the badge refetches on every tab switch — catches
    // approvals that happen outside the app (e.g., via the Teams card).
  }, [service, currentUser.email, isManager, refreshSignal, selected]);

  // Only Admin/Management see the Manager Queue. Employees who land on
  // ?tab=queue (or switch users while on it) get File leave instead.
  const tab: TabValue = selected === "queue" && !isManager ? "file" : selected;
  useEffect(() => {
    if (selected === "queue" && !isManager) {
      setSelected("file");
      syncTabToUrl("file");
    }
  }, [selected, isManager]);

  return (
    <div className={s.app}>
      <div className={s.topbar}>
      <div className={s.header}>
        <div className={s.brand}>
          <span className={s.logoPlate}>
            <img className={s.logoImg} src={kationLogo} alt="Kation Technologies" />
          </span>
          <Text weight="bold" size={400}>
            Kation People
          </Text>
          <Badge appearance="tint" color="brand">
            Leave
          </Badge>
          {dataMode === "mock" && (
            <Badge appearance="tint" color="warning">
              Demo data
            </Badge>
          )}
        </div>
        <div className={s.user}>
          {dataMode === "mock" && switchableUsers.length > 1 && (
            <div className={s.viewAs}>
              <Text size={200} className={s.viewAsLabel}>
                Viewing as
              </Text>
              <Dropdown
                size="small"
                value={currentUser.name}
                selectedOptions={[currentUser.email]}
                onOptionSelect={(_, d) => d.optionValue && switchUser(d.optionValue)}
                aria-label="Viewing as (demo)"
              >
                {switchableUsers.map((u) => (
                  <Option key={u.email} value={u.email} text={u.name}>
                    {u.name}
                  </Option>
                ))}
              </Dropdown>
            </div>
          )}
          <Text size={300}>{currentUser.name}</Text>
          {employee?.department && (
            <Badge appearance="outline" color={isManager ? "brand" : "informative"}>
              {employee.department}
            </Badge>
          )}
          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              <button className={s.avatarButton} aria-label="Account menu">
                <Avatar name={currentUser.name} image={photoUrl ? { src: photoUrl } : undefined} size={28} color="colorful" />
              </button>
            </MenuTrigger>
            <MenuPopover>
              <div className={s.menuHeader}>
                <Text weight="semibold" size={300}>
                  {currentUser.name}
                </Text>
                <Text size={200} className={s.menuEmail}>
                  {currentUser.email}
                </Text>
              </div>
              {dataMode === "graph" && (
                <>
                  <Divider />
                  <MenuList>
                    <MenuItem icon={<SignOut24Regular />} onClick={signOut}>
                      Sign out
                    </MenuItem>
                  </MenuList>
                </>
              )}
            </MenuPopover>
          </Menu>
        </div>
      </div>

      <div className={s.tabs}>
        <TabList
          selectedValue={tab}
          onTabSelect={(_, d) => {
            setSelected(d.value as TabValue);
            syncTabToUrl(d.value as TabValue);
          }}
          size="large"
        >
          <Tab value="file" icon={<DocumentAdd24Regular />}>
            File leave
          </Tab>
          <Tab value="mine" icon={<ClipboardTaskListLtr24Regular />}>
            My leaves
          </Tab>
          {isManager && (
            <Tab value="queue" icon={<PeopleTeam24Regular />}>
              <span className={s.tabLabel}>
                Manager queue
                {pendingCount > 0 && (
                  <CounterBadge count={pendingCount} color="danger" size="small" />
                )}
              </span>
            </Tab>
          )}
          <Tab value="calendar" icon={<CalendarLtr24Regular />}>
            Team calendar
          </Tab>
        </TabList>
      </div>
      </div>

      <div className={s.content}>
        {tab === "file" && <FileLeave />}
        {tab === "mine" && (
          <MyLeaves
            onFileLeave={() => {
              setSelected("file");
              syncTabToUrl("file");
            }}
          />
        )}
        {tab === "queue" && <ManagerQueue />}
        {tab === "calendar" && <TeamCalendar />}
      </div>

      <Text as="span" size={100} className={s.version} title={`KTI Leave Management ${APP_VERSION_LABEL}`}>
        {APP_VERSION_LABEL}
      </Text>
    </div>
  );
}

export default function App() {
  const theme = useTeamsTheme();
  return (
    <FluentProvider theme={theme}>
      <AppProvider>
        <Shell />
        <AskKatie />
      </AppProvider>
    </FluentProvider>
  );
}
